import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

export default function Leaderboard() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(`/api/student/exam/${token}/leaderboard`)
      .then(r => r.json())
      .then(d => { if (d.error) setError(d.error); else setData(d); })
      .catch(() => setError('Failed to load leaderboard'));
  }, [token]);

  const medal = (rank) => rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : `#${rank}`;

  if (error) return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center', color: 'var(--text2)' }}>
        <div style={{ fontSize: 48 }}>😕</div>
        <p style={{ marginTop: 12 }}>{error}</p>
      </div>
    </div>
  );

  if (!data) return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 40, height: 40, border: '3px solid #2e2e50', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  const top3 = data.leaderboard.slice(0, 3);
  const rest = data.leaderboard.slice(3);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', fontFamily: 'Sora, sans-serif', color: 'var(--text)' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&display=swap');
        :root{--bg:#0f0f1a;--bg2:#1a1a2e;--card:#1e1e35;--card2:#252542;--border:#2e2e50;--text:#e2e8f0;--text2:#94a3b8;--text3:#64748b;--primary:#6366f1;--success:#10b981;--warning:#f59e0b;}
        *{margin:0;padding:0;box-sizing:border-box;}
        body{background:var(--bg);color:var(--text);font-family:'Sora',sans-serif;}
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes fadeIn{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.6}}
      `}</style>

      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #1e1e35 0%, #16213e 100%)', borderBottom: '1px solid var(--border)', padding: '32px 20px', textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 8 }}>🏆</div>
        <h1 style={{ fontSize: 28, fontWeight: 800, marginBottom: 6 }}>{data.title}</h1>
        <p style={{ color: 'var(--text2)', fontSize: 14 }}>{data.subject} • {data.leaderboard.length} participants</p>
      </div>

      <div style={{ maxWidth: 700, margin: '0 auto', padding: '32px 20px' }}>

        {/* Top 3 Podium */}
        {top3.length > 0 && (
          <div style={{ marginBottom: 32, animation: 'fadeIn 0.4s ease' }}>
            <h2 style={{ textAlign: 'center', fontWeight: 700, marginBottom: 24, color: 'var(--text2)', fontSize: 14, textTransform: 'uppercase', letterSpacing: 1 }}>Top Performers</h2>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-end', gap: 12 }}>
              {/* Reorder: 2nd, 1st, 3rd */}
              {[top3[1], top3[0], top3[2]].filter(Boolean).map((p, idx) => {
                const isFirst = p.rank === 1;
                const heights = { 1: 130, 2: 100, 3: 80 };
                const h = heights[p.rank] || 80;
                const colors = { 1: '#f59e0b', 2: '#94a3b8', 3: '#cd7f32' };
                const bgColors = { 1: 'rgba(245,158,11,0.15)', 2: 'rgba(148,163,184,0.1)', 3: 'rgba(205,127,50,0.1)' };
                return (
                  <div key={p.rank} style={{ textAlign: 'center', flex: 1, maxWidth: 180, animation: `fadeIn 0.4s ease ${idx * 0.1}s both` }}>
                    <div style={{ fontSize: isFirst ? 36 : 28, marginBottom: 6 }}>{medal(p.rank)}</div>
                    <div style={{ fontWeight: 700, fontSize: isFirst ? 15 : 13, marginBottom: 4, color: 'var(--text)' }}>{p.student_name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 8 }}>{p.score}/{p.total_marks} • {p.percentage}%</div>
                    <div style={{
                      height: h, background: bgColors[p.rank], border: `2px solid ${colors[p.rank]}`,
                      borderRadius: '12px 12px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexDirection: 'column', gap: 4
                    }}>
                      <div style={{ fontWeight: 800, fontSize: 22, color: colors[p.rank] }}>{p.percentage}%</div>
                      <div style={{ fontSize: 11, color: 'var(--text3)' }}>{Math.floor((p.time_used || 0) / 60)}m used</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Rest of Leaderboard */}
        {rest.length > 0 && (
          <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 16, overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', fontWeight: 700, fontSize: 14 }}>
              All Rankings
            </div>
            {rest.map((p, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 16, padding: '14px 20px',
                borderBottom: i < rest.length - 1 ? '1px solid var(--border)' : 'none',
                animation: `fadeIn 0.3s ease ${i * 0.05}s both`
              }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--card2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, color: 'var(--text2)', flexShrink: 0 }}>
                  {p.rank}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{p.student_name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text3)' }}>{Math.floor((p.time_used || 0) / 60)}m used</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: 700 }}>{p.score}/{p.total_marks}</div>
                  <div style={{
                    fontSize: 12, fontWeight: 600,
                    color: p.percentage >= 80 ? '#34d399' : p.percentage >= 50 ? '#fcd34d' : '#f87171'
                  }}>{p.percentage}%</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {data.leaderboard.length === 0 && (
          <div style={{ textAlign: 'center', padding: 60, color: 'var(--text2)' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>📭</div>
            <p>No submissions yet. Be the first!</p>
          </div>
        )}

        <p style={{ textAlign: 'center', marginTop: 32, fontSize: 12, color: 'var(--text3)' }}>
          Powered by Oexams • Auto-refreshes every 30 seconds
        </p>
      </div>
    </div>
  );
}
